import Vue, { CreateElement, VNode } from 'vue';
// eslint-disable-next-line import/no-unresolved, import/extensions
import { PDFDocumentLoadingTask, PDFDocumentProxy, PDFPageProxy } from 'pdfjs-dist/types/src/display/api';
import lazy, { Lazy } from '@/util/lazy-val';

export interface PDFController {
  numberOfPages(): Promise<number>;
  page(pageNo: number): Promise<PDFPageProxy>;
}

export default Vue.extend({
  name: 'PDFContext',

  props: {
    url: { type: String, required: true },
    batchSize: { type: Number, default: 10 },
  },

  data() {
    return {
      controller: new PDFControllerImpl(this.url, this.batchSize) as PDFController,
    };
  },

  render(create: CreateElement): VNode {
    const children = this.$scopedSlots.default?.({ pdf: this.controller });
    if (children && children.length === 1) {
      return children[0];
    }
    return create('div', children);
  },

});

export class PDFControllerImpl implements PDFController {
  url: string;

  batchSize: number

  pdf: Lazy<Promise<PDFDocumentProxy>> = lazy(() => loadPdf(this.url))

  pages: PDFPageProxy[] = []

  constructor(url: string, batchSize: number) {
    this.url = url;
    this.batchSize = batchSize;
  }

  public async numberOfPages() {
    return (await this.pdf()).numPages;
  }

  public async page(pageNo: number) {
    const index = pageNo - 1;
    if (!this.pages[index]) {
      const res = await this.loadPages(index, this.batchSize);
      for (const [i, page] of res.entries()) {
        if (page) {
          this.pages[i + index] = page;
        }
      }
    }
    return this.pages[index];
  }

  async loadPages(index: number, amount: number) {
    const doc = await this.pdf();
    const pageRange = [...range(index + 1, Math.min(index + amount, doc.numPages))];
    const pages = await Promise.all(
      pageRange.map((pageNo) => doc.getPage(pageNo)
        .catch((e) => PDFControllerImpl.onError(`page(${pageNo})`, e))),
    );
    return pages;
  }

  static onError(ctx: string, e: any) {
    // eslint-disable-next-line no-console
    console.error(`Error processing PDF: ${ctx}: `, e);
  }
}

function loadPdf(url: string): Promise<PDFDocumentProxy> {
  return import(/* webpackChunkName: 'pdfjs-dist' */ 'pdfjs-dist/webpack')
    .then((pdfjs) => {
      const task: PDFDocumentLoadingTask = pdfjs.getDocument(url);
      return task.promise;
    });
}

function* range(start, end) {
  for (let i = start; i <= end; i++) {
    yield i;
  }
}
